import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTrip } from '../../contexts/TripContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Vote, CheckCircle2, Users, Crown, Trophy, MapPin, Loader2, Sparkles, Plus, Trash2, Image as ImageIcon, AlertTriangle, X, Pencil, ExternalLink, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import PlaceAutocomplete from '../common/PlaceAutocomplete';
import CoverImageUploader from '../common/CoverImageUploader';
import { Textarea } from '../ui/textarea';

export function DestinationVoting({ trip, members }) {
  const { user, api } = useAuth();
  const { voteForDestination, removeVoteForDestination, selectWinningDestination, addDestinationOption, removeDestinationOption, updateDestinationOption } = useTrip();
  const navigate = useNavigate();
  const [voting, setVoting] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [newOptionPlace, setNewOptionPlace] = useState(null);
  const [newOptionNumber, setNewOptionNumber] = useState('');
  const [newOptionComplement, setNewOptionComplement] = useState('');
  
  // Edit state
  const [editingOption, setEditingOption] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editImage, setEditImage] = useState('');
  const [editImageBlob, setEditImageBlob] = useState(null); // For deferred upload
  const [editPlace, setEditPlace] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // View details state
  const [viewingOption, setViewingOption] = useState(null);
  
  const isOrganizer = trip.my_role === 'organizer';
  const votingData = trip.destination_voting;
  
  if (!votingData || trip.status !== 'voting') {
    return null;
  }

  const totalVotes = votingData.options.reduce((sum, opt) => sum + (opt.votes?.length || 0), 0);
  const memberCount = members?.length || trip.member_count || 1;
  const optionsCount = votingData.options.length;
  const MAX_OPTIONS = 3;
  
  // Check if current user has voted
  const userVote = votingData.options.find(opt => opt.votes?.includes(user?.id));

  // Handle click on add button - check limit first
  const handleAddButtonClick = () => {
    if (optionsCount >= MAX_OPTIONS) {
      setLimitModalOpen(true);
    } else {
      setAddDialogOpen(true);
    }
  };

  const handleVote = async (optionId) => {
    setVoting(true);
    try {
      // Check if user already voted for this option
      const option = votingData.options.find(opt => opt.id === optionId || String(opt.id) === String(optionId));
      const hasVoted = option?.votes?.includes(user?.id);
      
      if (hasVoted) {
        // Remove vote
        await removeVoteForDestination(trip.id, optionId);
        toast.success('Voto removido! 🗳️');
      } else {
        // Add vote
        await voteForDestination(trip.id, optionId);
        toast.success('Voto registrado! 🗳️');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao processar voto';
      toast.error(message);
    } finally {
      setVoting(false);
    }
  };

  // Navigate to CreateTrip page with pre-filled data from voting
  const handleOpenFinalizeModal = (option) => {
    navigate('/trips/new', {
      state: {
        finalizeVoting: {
          tripId: trip.id,
          tripName: trip.name || '',
          selectedOption: {
            id: option.id,
            name: option.title || option.name,
            image: option.image,
            place_data: option.place_data,
            description: option.description || ''
          },
          startDate: trip.start_date || null,
          endDate: trip.end_date || null,
          coverImage: trip.cover_image || option.image,
          origin: trip.origin || '',
          originPlace: trip.origin_data || null,
          description: trip.description || option.description || ''
        }
      }
    });
  };

  const handleSelectWinner = async (optionId, optionName) => {
    setSelecting(true);
    try {
      await selectWinningDestination(trip.id, optionId);
      toast.success(`Destino escolhido: ${optionName}! 🎉`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao selecionar destino';
      toast.error(message);
    } finally {
      setSelecting(false);
    }
  };

  const handleAddOption = async () => {
    if (!newOptionPlace) {
      toast.error('Selecione um destino');
      return;
    }
    
    const destinationName = newOptionPlace.name || newOptionPlace.formatted_address || '';
    
    // Build place data with address components
    const buildAddressData = (place, number, complement) => {
      if (!place) return null;
      
      const getComponent = (types) => {
        if (!place.address_components) return null;
        const component = place.address_components.find(c => 
          types.some(t => c.types.includes(t))
        );
        return component?.long_name || null;
      };
      
      const getComponentShort = (types) => {
        if (!place.address_components) return null;
        const component = place.address_components.find(c => 
          types.some(t => c.types.includes(t))
        );
        return component?.short_name || null;
      };
      
      return {
        place_name: place.name || null,
        formatted_address: place.formatted_address || null,
        place_id: place.place_id || null,
        street: getComponent(['route', 'street_address']) || null,
        number: number || getComponent(['street_number']) || null,
        complement: complement || null,
        neighborhood: getComponent(['sublocality', 'sublocality_level_1', 'neighborhood']) || null,
        city: getComponent(['administrative_area_level_2', 'locality']) || null,
        state: getComponent(['administrative_area_level_1']) || null,
        state_code: getComponentShort(['administrative_area_level_1']) || null,
        country: getComponent(['country']) || null,
        country_code: getComponentShort(['country']) || null,
        postal_code: getComponent(['postal_code']) || null,
        lat: place.lat || null,
        lng: place.lng || null,
        google_maps_url: place.google_maps_url || null
      };
    };
    
    const placeData = buildAddressData(newOptionPlace, newOptionNumber, newOptionComplement);
    
    setAdding(true);
    try {
      await addDestinationOption(trip.id, destinationName, null, placeData);
      toast.success(`Opção "${destinationName}" adicionada! ✨`);
      setNewOptionPlace(null);
      setNewOptionNumber('');
      setNewOptionComplement('');
      setAddDialogOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao adicionar opção';
      toast.error(message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveOption = async (optionId, optionName) => {
    setRemoving(optionId);
    try {
      await removeDestinationOption(trip.id, optionId);
      toast.success(`Opção "${optionName}" removida`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao remover opção';
      toast.error(message);
    } finally {
      setRemoving(null);
    }
  };

  // Handle edit option
  const handleStartEdit = (option) => {
    setEditingOption(option);
    setEditTitle(option.title || '');
    setEditDescription(option.description || '');
    setEditImage(option.image || '');
    setEditUrl(option.url || '');
    // Create a place object from existing data
    if (option.place_data) {
      setEditPlace({
        name: option.name,
        formatted_address: option.place_data.formatted_address,
        place_id: option.place_data.place_id,
        lat: option.place_data.lat,
        lng: option.place_data.lng,
        ...option.place_data
      });
    } else {
      setEditPlace({ name: option.name });
    }
  };

  const handleSaveEdit = async () => {
    if (!editingOption) return;
    
    setSaving(true);
    try {
      // Upload deferred image blob if exists
      let finalImage = editImage;
      if (editImageBlob && editImage?.startsWith('blob:')) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', editImageBlob, 'cover.jpg');
        const response = await api.post('/storage/upload-image-direct', uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        finalImage = response.data.file_url;
      }

      const updateData = {
        title: editTitle || null,
        description: editDescription || null,
        url: editUrl || null,
        image: finalImage || null,
      };
      
      // Check if option has votes - don't allow location change
      const hasVotes = (editingOption.votes?.length || 0) > 0;
      
      // If place changed, update name and place_data
      if (editPlace && editPlace.name !== editingOption.name && !hasVotes) {
        updateData.name = editPlace.name || editPlace.formatted_address;
        updateData.place_data = {
          place_name: editPlace.name || null,
          formatted_address: editPlace.formatted_address || null,
          place_id: editPlace.place_id || null,
          lat: editPlace.lat || null,
          lng: editPlace.lng || null,
        };
      }
      
      await updateDestinationOption(trip.id, editingOption.id, updateData);
      toast.success('Opção atualizada! ✨');
      setEditingOption(null);
      setEditImageBlob(null); // Clear blob after save
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao atualizar opção';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingOption(null);
    setEditTitle('');
    setEditDescription('');
    setEditUrl('');
    setEditImage('');
    setEditImageBlob(null); // Clear blob on cancel
    setEditPlace(null);
  };

  // Sort options by vote count
  const sortedOptions = [...votingData.options].sort((a, b) => 
    (b.votes?.length || 0) - (a.votes?.length || 0)
  );

  const leadingOption = sortedOptions[0];
  const isLeading = (optionId) => leadingOption?.id === optionId && (leadingOption?.votes?.length || 0) > 0;

  return (
    <Card className="border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-slate-900">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Vote className="w-5 h-5 text-purple-500" />
          <CardTitle className="text-purple-900 dark:text-purple-300">Votação de Destino</CardTitle>
          <Badge variant="outline" className="ml-auto border-purple-300 text-purple-700 dark:border-purple-700 dark:text-purple-300">
            {totalVotes} voto{totalVotes !== 1 ? 's' : ''}
          </Badge>
        </div>
        <CardDescription className="dark:text-purple-400">
          {userVote 
            ? `Você votou em "${userVote.name}". Pode mudar seu voto a qualquer momento.`
            : 'Vote no destino que você prefere para essa viagem!'
          }
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Add Option Button (Organizer only) */}
        {isOrganizer && (
          <>
            {/* Limit Modal */}
            <Dialog open={limitModalOpen} onOpenChange={setLimitModalOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-amber-600">
                    <AlertTriangle className="w-5 h-5" />
                    Limite de opções atingido
                  </DialogTitle>
                  <DialogDescription>
                    Você já cadastrou o máximo de {MAX_OPTIONS} destinos para votação.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Para adicionar um novo destino, é necessário remover uma das opções existentes primeiro.
                  </p>
                </div>
                <DialogFooter>
                  <Button onClick={() => setLimitModalOpen(false)}>
                    Entendi
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Inline Add Option Form - expands when button is clicked */}
            {!addDialogOpen ? (
              <Button
                variant="outline"
                className="w-full border-dashed border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                onClick={handleAddButtonClick}
                data-testid="add-destination-option-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar opção de destino ({optionsCount}/{MAX_OPTIONS})
              </Button>
            ) : (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="border border-purple-300 dark:border-purple-700 rounded-xl p-4 bg-purple-50/50 dark:bg-purple-900/20"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-purple-700 dark:text-purple-300">
                      Adicionar nova opção
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAddDialogOpen(false);
                        setNewOptionPlace(null);
                        setNewOptionNumber('');
                        setNewOptionComplement('');
                      }}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">Novo destino *</Label>
                    <PlaceAutocomplete
                      value={newOptionPlace}
                      onChange={(place) => setNewOptionPlace(place)}
                      placeholder="Cidade, endereço ou local..."
                      showMap={true}
                      data-testid="new-option-place"
                    />
                  </div>
                  
                  {/* Optional Number and Complement fields */}
                  {newOptionPlace && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Input
                          placeholder="Número (opcional)"
                          value={newOptionNumber}
                          onChange={(e) => setNewOptionNumber(e.target.value)}
                          className="dark:bg-slate-800 dark:border-slate-700 text-sm"
                          data-testid="new-option-number"
                        />
                      </div>
                      <div>
                        <Input
                          placeholder="Complemento (opcional)"
                          value={newOptionComplement}
                          onChange={(e) => setNewOptionComplement(e.target.value)}
                          className="dark:bg-slate-800 dark:border-slate-700 text-sm"
                          data-testid="new-option-complement"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setAddDialogOpen(false);
                        setNewOptionPlace(null);
                        setNewOptionNumber('');
                        setNewOptionComplement('');
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleAddOption}
                      disabled={adding || !newOptionPlace}
                      className="flex-1 bg-purple-500 hover:bg-purple-600"
                      data-testid="confirm-add-option-btn"
                    >
                      {adding ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Adicionar
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}

        {/* Destination Cards Carousel - Mobile: horizontal swipe with peek effect, Desktop: grid */}
        <div className="sm:hidden -mx-4">
          <div 
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-4 px-6 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {sortedOptions.map((option, index) => {
              const voteCount = option.votes?.length || 0;
              const percentage = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
              const hasVoted = option.votes?.includes(user?.id);
              const canRemove = isOrganizer && optionsCount > 1;
              
              return (
                <motion.div
                  key={option.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.1 }}
                  className={cn(
                    'flex-shrink-0 w-[75vw] max-w-[300px] snap-center rounded-2xl border-2 overflow-hidden transition-all relative',
                    // Leading card gets special highlight
                    isLeading(option.id) && totalVotes > 0
                      ? 'border-yellow-400 bg-gradient-to-b from-yellow-50 to-white dark:from-yellow-900/20 dark:to-slate-900 shadow-xl shadow-yellow-200/50 dark:shadow-yellow-900/30 ring-2 ring-yellow-300/50'
                      : hasVoted 
                        ? 'border-purple-500 bg-gradient-to-b from-purple-50 to-white dark:from-purple-900/40 dark:to-slate-900 shadow-lg' 
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg'
                  )}
                >
                  {/* Leading indicator badge */}
                  {isLeading(option.id) && totalVotes > 0 && (
                    <div className="absolute top-3 right-3 z-10">
                      <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white shadow-lg animate-pulse">
                        <Trophy className="w-3 h-3 mr-1" />
                        Liderando
                      </Badge>
                    </div>
                  )}

                  {/* Image or placeholder - larger */}
                  <div className={cn(
                    "relative w-full h-36 bg-gradient-to-br",
                    isLeading(option.id) && totalVotes > 0
                      ? "from-yellow-100 to-amber-50 dark:from-yellow-900/30 dark:to-slate-700"
                      : "from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700"
                  )}>
                    {option.image ? (
                      <img 
                        src={option.image} 
                        alt={option.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {isLeading(option.id) && totalVotes > 0 ? (
                          <Trophy className="w-14 h-14 text-yellow-500 dark:text-yellow-400" />
                        ) : (
                          <MapPin className="w-14 h-14 text-slate-400 dark:text-slate-500" />
                        )}
                      </div>
                    )}
                    {/* Vote percentage overlay */}
                    {totalVotes > 0 && (
                      <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-slate-200 dark:bg-slate-700">
                        <div 
                          className={cn(
                            "h-full transition-all duration-500",
                            isLeading(option.id) ? "bg-yellow-500" : "bg-purple-500"
                          )}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-3">
                    {/* Custom Title or Name */}
                    <h4 className="font-bold text-base text-slate-900 dark:text-white leading-tight">
                      {option.title || option.name}
                    </h4>
                    
                    {/* Location name (show separately if has custom title) */}
                    {option.title && (
                      <div className="flex items-start gap-1.5 text-slate-600 dark:text-slate-400">
                        <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{option.name}</span>
                      </div>
                    )}
                    
                    {/* Full address (always show if available) */}
                    {option.place_data?.formatted_address && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        {option.place_data.formatted_address}
                      </p>
                    )}

                    {/* Description (limited to 200 chars) */}
                    {/* Description (limited to 2 lines) */}
                    {option.description && (
                      <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
                        {option.description}
                      </p>
                    )}

                    {/* URL link (if set) */}
                    {option.url && (
                      <a
                        href={option.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`option-url-${option.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Ver mais informações
                      </a>
                    )}

                    {/* Vote stats */}
                    <div className="flex items-center gap-3 pt-1">
                      <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                        <Users className="w-4 h-4" />
                        <span className="font-medium">{voteCount}</span>
                        <span className="text-sm">voto{voteCount !== 1 ? 's' : ''}</span>
                      </div>
                      {totalVotes > 0 && (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
                          {Math.round(percentage)}%
                        </Badge>
                      )}
                      {hasVoted && (
                        <Badge className="bg-purple-500 text-white">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Seu voto
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Footer actions with labels */}
                  <div className="border-t border-slate-100 dark:border-slate-700 p-2">
                    <div className="grid grid-cols-5 gap-1">
                      {/* Vote/Remove vote button */}
                      <Button
                        variant={hasVoted ? "default" : "ghost"}
                        onClick={() => handleVote(option.id)}
                        disabled={voting}
                        className={cn(
                          "h-auto py-1.5 px-1 flex-col gap-0.5 text-[10px]",
                          hasVoted 
                            ? "bg-purple-500 hover:bg-red-500 text-white" 
                            : "text-purple-600 hover:bg-purple-50 dark:text-purple-400"
                        )}
                        data-testid={`vote-option-${option.id}`}
                      >
                        {voting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : hasVoted ? (
                          <X className="w-4 h-4" />
                        ) : (
                          <Vote className="w-4 h-4" />
                        )}
                        <span className="font-medium">{hasVoted ? 'Remover' : 'Votar'}</span>
                      </Button>

                      {/* View details button */}
                      <Button
                        variant="ghost"
                        onClick={() => setViewingOption(option)}
                        className="h-auto py-1.5 px-1 flex-col gap-0.5 text-[10px] text-slate-600 hover:bg-slate-50 dark:text-slate-400"
                        data-testid={`view-option-${option.id}`}
                      >
                        <Eye className="w-4 h-4" />
                        <span className="font-medium">Detalhes</span>
                      </Button>

                      {/* Organizer actions */}
                      {isOrganizer ? (
                        <>
                          {/* Edit button */}
                          <Button
                            variant="ghost"
                            onClick={() => handleStartEdit(option)}
                            className="h-auto py-1.5 px-1 flex-col gap-0.5 text-[10px] text-blue-600 hover:bg-blue-50 dark:text-blue-400"
                            data-testid={`edit-option-${option.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                            <span className="font-medium">Editar</span>
                          </Button>

                          {/* Select winner button - opens finalize modal */}
                          <Button
                            variant="ghost"
                            className="h-auto py-1.5 px-1 flex-col gap-0.5 text-[10px] text-green-600 hover:bg-green-50 dark:text-green-400"
                            data-testid={`select-winner-${option.id}`}
                            onClick={() => handleOpenFinalizeModal(option)}
                          >
                            <Crown className="w-4 h-4" />
                            <span className="font-medium">Escolher</span>
                          </Button>

                          {/* Remove button */}
                          {canRemove ? (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className="h-auto py-1.5 px-1 flex-col gap-0.5 text-[10px] text-red-500 hover:bg-red-50 dark:text-red-400"
                                  data-testid={`remove-option-${option.id}`}
                                >
                                  {removing === option.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                  <span className="font-medium">Excluir</span>
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remover opção</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja remover <strong>{option.title || option.name}</strong> da votação?
                                    {voteCount > 0 && (
                                      <span className="block mt-2 text-orange-600">
                                        Atenção: Esta opção tem {voteCount} voto{voteCount !== 1 ? 's' : ''} que serão perdidos.
                                      </span>
                                    )}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleRemoveOption(option.id, option.title || option.name)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Remover
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          ) : (
                            <div /> /* Empty space placeholder */
                          )}
                        </>
                      ) : (
                        /* Non-organizer: fill remaining grid space */
                        <>
                          <div />
                          <div />
                          <div />
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          {/* Carousel indicators */}
          {sortedOptions.length > 1 && (
            <div className="flex justify-center gap-2 mt-2">
              {sortedOptions.map((option, index) => (
                <div 
                  key={option.id}
                  className={cn(
                    "w-2 h-2 rounded-full transition-colors",
                    index === 0 ? "bg-purple-500" : "bg-slate-300 dark:bg-slate-600"
                  )}
                />
              ))}
            </div>
          )}
        </div>

        {/* Desktop: Original vertical layout */}
        <div className="hidden sm:block space-y-4">
          <AnimatePresence>
            {sortedOptions.map((option, index) => {
              const voteCount = option.votes?.length || 0;
              const percentage = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
              const hasVoted = option.votes?.includes(user?.id);
              const canRemove = isOrganizer && optionsCount > 1;
              
              return (
                <motion.div
                  key={option.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={cn(
                    'relative p-4 rounded-xl border-2 transition-all',
                    hasVoted 
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30' 
                      : 'border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600'
                  )}
                >
                  {/* Leading indicator */}
                  {isLeading(option.id) && (
                    <div className="absolute -top-2 -right-2">
                      <Badge className="bg-yellow-500 text-white">
                        <Trophy className="w-3 h-3 mr-1" />
                        Liderando
                      </Badge>
                    </div>
                  )}

                  <div className="flex items-start gap-4">
                    {/* Image or placeholder */}
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 flex-shrink-0">
                      {option.image ? (
                        <img 
                          src={option.image} 
                          alt={option.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <MapPin className="w-8 h-8 text-slate-400" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Custom Title or Name */}
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-900 dark:text-white truncate">
                          {option.title || option.name}
                        </h4>
                        {hasVoted && (
                          <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0" />
                        )}
                      </div>
                      
                      {/* Show location name separately if has custom title */}
                      {option.title && (
                        <div className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 mb-1">
                          <MapPin className="w-3.5 h-3.5" />
                          <span className="truncate">{option.name}</span>
                        </div>
                      )}

                      {/* Full address (if available) */}
                      {option.place_data?.formatted_address && (
                        <p className="text-xs text-slate-400 dark:text-slate-500 mb-1">
                          {option.place_data.formatted_address}
                        </p>
                      )}

                      {/* Description (if set) - limited to 200 chars */}
                      {option.description && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-2 line-clamp-1">
                          {option.description.length > 200 
                            ? `${option.description.substring(0, 200)}...` 
                            : option.description}
                        </p>
                      )}

                      {/* URL link (if set) */}
                      {option.url && (
                        <a
                          href={option.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline mb-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3 h-3" />
                          Ver mais
                        </a>
                      )}

                      <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-2">
                        <Users className="w-4 h-4" />
                        <span>{voteCount} voto{voteCount !== 1 ? 's' : ''}</span>
                        {totalVotes > 0 && (
                          <span className="text-purple-600 dark:text-purple-400 font-medium">
                            ({Math.round(percentage)}%)
                          </span>
                        )}
                      </div>

                      <Progress 
                        value={percentage} 
                        className="h-2 bg-slate-100 dark:bg-slate-700"
                      />
                    </div>

                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {/* View details button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewingOption(option)}
                        className="text-xs text-slate-500 hover:text-purple-600"
                        data-testid={`view-option-desktop-${option.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Detalhes
                      </Button>

                      {/* Edit button for organizer */}
                      {isOrganizer && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStartEdit(option)}
                          className="text-xs text-slate-500 hover:text-blue-600"
                          data-testid={`edit-option-desktop-${option.id}`}
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Editar
                        </Button>
                      )}
                      
                      <Button
                        variant={hasVoted ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleVote(option.id)}
                        disabled={voting}
                        className={cn(
                          "rounded-full",
                          hasVoted && "bg-purple-500 hover:bg-red-500"
                        )}
                        data-testid={`vote-option-${option.id}`}
                      >
                        {voting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : hasVoted ? (
                          <>
                            <X className="w-4 h-4 mr-1" />
                            Remover
                          </>
                        ) : (
                          'Votar'
                        )}
                      </Button>

                      {isOrganizer && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs text-slate-500 hover:text-green-600"
                          data-testid={`select-winner-${option.id}`}
                          onClick={() => handleOpenFinalizeModal(option)}
                        >
                          <Crown className="w-3 h-3 mr-1" />
                          Escolher
                        </Button>
                      )}

                      {/* Remove option button (organizer only, min 1 option) */}
                      {canRemove && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs text-slate-400 hover:text-red-500"
                              data-testid={`remove-option-${option.id}`}
                            >
                              {removing === option.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Trash2 className="w-3 h-3" />
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remover opção</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja remover <strong>{option.name}</strong> da votação?
                                {voteCount > 0 && (
                                  <span className="block mt-2 text-orange-600">
                                    Atenção: Esta opção tem {voteCount} voto{voteCount !== 1 ? 's' : ''} que serão perdidos.
                                  </span>
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleRemoveOption(option.id, option.name)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remover
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Info for organizer */}
        {isOrganizer && (
          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-orange-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-orange-900 dark:text-orange-300">
                  Dica para organizadores
                </p>
                <p className="text-sm text-orange-700 dark:text-orange-400 mt-1">
                  Você pode adicionar ou remover opções de destino a qualquer momento (mínimo 1 opção). 
                  Quando escolher o destino vencedor, a viagem será ativada com todas as funcionalidades.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Participation stats */}
        <div className="flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-700">
          <Users className="w-4 h-4" />
          <span>
            {totalVotes} de {memberCount} membro{memberCount !== 1 ? 's' : ''} votaram
          </span>
          {totalVotes === memberCount && totalVotes > 0 && (
            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              Todos votaram!
            </Badge>
          )}
        </div>

        {/* Edit Option Dialog */}
        <Dialog open={!!editingOption} onOpenChange={(open) => !open && handleCancelEdit()}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-blue-500" />
                Editar opção de destino
              </DialogTitle>
              <DialogDescription>
                Adicione mais detalhes sobre este destino para ajudar os viajantes a decidirem.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Custom title */}
              <div className="space-y-2">
                <Label>Título personalizado (opcional)</Label>
                <Input
                  placeholder="Ex: Fim de semana na praia"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="dark:bg-slate-800 dark:border-slate-700"
                />
                <p className="text-xs text-slate-500">Um título atrativo para destacar este destino</p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Descrição (opcional)</Label>
                <Textarea
                  placeholder="Ex: Praia tranquila com ótimos restaurantes e vida noturna..."
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="dark:bg-slate-800 dark:border-slate-700 min-h-[80px]"
                />
              </div>

              {/* URL */}
              <div className="space-y-2">
                <Label>Link/URL (opcional)</Label>
                <Input
                  placeholder="https://exemplo.com/destino"
                  value={editUrl}
                  onChange={(e) => setEditUrl(e.target.value)}
                  className="dark:bg-slate-800 dark:border-slate-700"
                  data-testid="edit-option-url"
                />
                <p className="text-xs text-slate-500">Link para mais informações sobre este destino</p>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Local
                  {(editingOption?.votes?.length || 0) > 0 && (
                    <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Bloqueado
                    </Badge>
                  )}
                </Label>
                {(editingOption?.votes?.length || 0) > 0 ? (
                  <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <p className="text-sm text-orange-700 dark:text-orange-300 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Este destino já possui {editingOption.votes.length} voto{editingOption.votes.length !== 1 ? 's' : ''}. 
                        Para alterar o local, é necessário <strong>excluir</strong> esta opção e cadastrar uma nova.
                      </span>
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 pl-6">
                      Local atual: <strong>{editPlace?.name || editPlace?.formatted_address || editingOption?.name}</strong>
                    </p>
                  </div>
                ) : (
                  <>
                    <PlaceAutocomplete
                      value={editPlace}
                      onChange={(place) => setEditPlace(place)}
                      placeholder="Buscar outro local..."
                      showMap={false}
                      data-testid="edit-option-place"
                    />
                    {editPlace && (
                      <p className="text-xs text-slate-500">
                        Atual: {editPlace.name || editPlace.formatted_address}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Cover image */}
              <div className="space-y-2">
                <Label>Imagem de capa (opcional)</Label>
                <CoverImageUploader
                  currentCoverUrl={editImage}
                  onCoverChange={(url) => setEditImage(url)}
                  deferUpload={true}
                  onBlobReady={(blob) => setEditImageBlob(blob)}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCancelEdit}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveEdit}
                disabled={saving}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Salvar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Details Modal */}
        <Dialog open={!!viewingOption} onOpenChange={(open) => !open && setViewingOption(null)}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-500" />
                Detalhes do Destino
              </DialogTitle>
            </DialogHeader>
            
            {viewingOption && (
              <div className="space-y-4 py-2">
                {/* Cover image */}
                {viewingOption.image ? (
                  <div className="w-full h-48 rounded-xl overflow-hidden">
                    <img 
                      src={viewingOption.image} 
                      alt={viewingOption.title || viewingOption.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-full h-32 rounded-xl bg-gradient-to-br from-purple-100 to-purple-50 dark:from-purple-900/30 dark:to-slate-800 flex items-center justify-center">
                    <MapPin className="w-12 h-12 text-purple-400" />
                  </div>
                )}

                {/* Title */}
                <div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                    {viewingOption.title || viewingOption.name}
                  </h3>
                  {viewingOption.title && (
                    <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 mt-1">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">{viewingOption.name}</span>
                    </div>
                  )}
                </div>

                {/* Full Address */}
                {viewingOption.place_data?.formatted_address && (
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <Label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Endereço completo</Label>
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      {viewingOption.place_data.formatted_address}
                    </p>
                  </div>
                )}

                {/* Description */}
                {viewingOption.description && (
                  <div>
                    <Label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Descrição</Label>
                    <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                      {viewingOption.description}
                    </p>
                  </div>
                )}

                {/* URL */}
                {viewingOption.url && (
                  <div>
                    <Label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Link</Label>
                    <a
                      href={viewingOption.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                    >
                      <ExternalLink className="w-4 h-4 flex-shrink-0" />
                      {viewingOption.url}
                    </a>
                  </div>
                )}

                {/* Vote stats */}
                <div className="flex items-center gap-4 pt-2 border-t border-slate-100 dark:border-slate-700">
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <Users className="w-5 h-5" />
                    <span className="font-medium">{viewingOption.votes?.length || 0}</span>
                    <span className="text-sm">voto{(viewingOption.votes?.length || 0) !== 1 ? 's' : ''}</span>
                  </div>
                  {totalVotes > 0 && (
                    <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
                      {Math.round(((viewingOption.votes?.length || 0) / totalVotes) * 100)}%
                    </Badge>
                  )}
                  {viewingOption.votes?.includes(user?.id) && (
                    <Badge className="bg-purple-500 text-white">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Seu voto
                    </Badge>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setViewingOption(null)}
                className="w-full sm:w-auto"
              >
                Fechar
              </Button>
              <Button
                onClick={() => {
                  const opt = viewingOption;
                  setViewingOption(null);
                  handleVote(opt.id);
                }}
                disabled={voting}
                className={cn(
                  "w-full sm:w-auto",
                  viewingOption?.votes?.includes(user?.id) 
                    ? "bg-red-500 hover:bg-red-600" 
                    : "bg-purple-500 hover:bg-purple-600"
                )}
              >
                {viewingOption?.votes?.includes(user?.id) ? (
                  <>
                    <X className="w-4 h-4 mr-2" />
                    Remover voto
                  </>
                ) : (
                  <>
                    <Vote className="w-4 h-4 mr-2" />
                    Votar neste destino
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
